import * as Sentry from '@sentry/vue';

const FILTERED_VALUE = '[Filtered]';
const SENSITIVE_KEY_PATTERN = /authorization|cookie|jwt|password|passwd|secret|token|api[-_]?key|config|request|payload|body/i;
const JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*\b/g;
const BEARER_PATTERN = /Bearer\s+[A-Za-z0-9._~+\/-]+=*/gi;

function redactString(value) {
  return value
    .replace(BEARER_PATTERN, `Bearer ${FILTERED_VALUE}`)
    .replace(JWT_PATTERN, FILTERED_VALUE);
}

function sanitize(value, seen = new WeakSet()) {
  if (typeof value === 'string') {
    return redactString(value);
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (seen.has(value)) {
    return '[Circular]';
  }
  seen.add(value);

  if (Array.isArray(value)) {
    return value.map(item => sanitize(item, seen));
  }

  return Object.keys(value).reduce((result, key) => {
    result[key] = SENSITIVE_KEY_PATTERN.test(key)
      ? FILTERED_VALUE
      : sanitize(value[key], seen);
    return result;
  }, {});
}

function getErrorMessage(error) {
  if (error && typeof error.message === 'string') {
    return redactString(error.message);
  }
  if (error && typeof error.error_message === 'string') {
    return redactString(error.error_message);
  }
  if (typeof error === 'string') {
    return redactString(error);
  }
  return 'Unknown error';
}

function createReportableError(error) {
  const reportableError = new Error(getErrorMessage(error));

  if (error && typeof error.name === 'string') {
    reportableError.name = redactString(error.name);
  }
  if (error && typeof error.stack === 'string') {
    reportableError.stack = redactString(error.stack);
  }

  return reportableError;
}

function sanitizeEvent(event) {
  const eventWithoutRequest = { ...event };

  // Browser request details are not needed for frontend exception tracking.
  delete eventWithoutRequest.request;

  return sanitize(eventWithoutRequest);
}

export function initErrorReporting(Vue) {
  Sentry.init({
    Vue,
    dsn: 'https://6f7e8d32d659c2886c4dd2a99c3f478a@o4507611765342208.ingest.us.sentry.io/4507611768225792',
    environment: 'frontend',
    // Ties Sentry events back to the exact deployed frontend build (git
    // commit sha, see vue.config.js) so a report can be traced to source.
    release: process.env.VUE_APP_BUILD_ID || undefined,
    integrations: defaultIntegrations =>
      defaultIntegrations.filter(integration => integration.name !== 'Breadcrumbs'),
    maxBreadcrumbs: 0,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend(event) {
      return sanitizeEvent(event);
    }
  });

  Vue.prototype.$captureException = captureException;
}

export function captureException(error, context = {}) {
  try {
    return Sentry.captureException(createReportableError(error), {
      extra: sanitize(context)
    });
  } catch (reportingError) {
    // Error reporting must never interfere with the application error flow.
    return undefined;
  }
}
